import type { CalculatorDefinition } from './types';

function numberValue(value: unknown): number {
  return Number(value);
}

function roundWon(value: number): number {
  return Math.round(value);
}

export const supplyPriceReverseCalculator: CalculatorDefinition = {
  id: 'supply-price-reverse',
  slug: 'supply-price-reverse',
  title: '공급가액 역산 계산기',
  description: '부가세 포함 합계금액에서 공급가액과 부가세를 역산합니다.',
  category: 'tax',
  aliases: ['공급가액 역산', '공급가액', '부가세 역산', 'VAT 역산', '합계금액 역산'],
  inputs: [
    { id: 'totalAmount', label: '부가세 포함 금액', type: 'number', unit: '원', min: 0, step: 100 },
  ],
  outputs: [
    { id: 'supplyAmount', label: '공급가액', unit: '원', format: 'currency', primary: true },
    { id: 'vatAmount', label: '부가세', unit: '원', format: 'currency' },
    { id: 'totalAmount', label: '합계금액', unit: '원', format: 'currency' },
  ],
  calculate: (values) => {
    const totalAmount = numberValue(values.totalAmount);
    const supplyAmount = totalAmount / 1.1;
    return {
      supplyAmount: roundWon(supplyAmount),
      vatAmount: roundWon(totalAmount - supplyAmount),
      totalAmount: roundWon(totalAmount),
    };
  },
  seo: {
    title: '공급가액 역산 계산기 | 부가세 포함 금액에서 공급가액 계산',
    description: '부가세 포함 합계금액을 입력하면 공급가액과 부가세를 10% VAT 기준으로 역산합니다.',
    canonicalPath: '/calculators/supply-price-reverse',
  },
  related: ['vat', 'margin', 'target-margin-price'],
};

export const withholding33Calculator: CalculatorDefinition = {
  id: 'withholding-33',
  slug: 'withholding-33',
  title: '3.3% 원천징수 계산기',
  description: '3.3% 원천징수 전 금액과 실수령액을 양방향으로 계산합니다.',
  category: 'tax',
  aliases: ['3.3%', '3.3', '원천징수', '원천세', '실수령액', '프리랜서 세금', '세전 금액 역산'],
  inputs: [
    {
      id: 'mode',
      label: '계산 방식',
      type: 'select',
      options: [
        { label: '세전 금액 → 실수령액', value: 'gross-to-net' },
        { label: '실수령액 → 세전 금액', value: 'net-to-gross' },
      ],
    },
    { id: 'amount', label: '금액', type: 'number', unit: '원', min: 0, step: 100 },
  ],
  outputs: [
    { id: 'grossAmount', label: '세전 금액', unit: '원', format: 'currency', primary: true },
    { id: 'withholdingAmount', label: '3.3% 원천징수', unit: '원', format: 'currency' },
    { id: 'netAmount', label: '실수령액', unit: '원', format: 'currency' },
  ],
  calculate: (values) => {
    const amount = numberValue(values.amount);
    const mode = String(values.mode);

    if (mode === 'net-to-gross') {
      const grossAmount = roundWon(amount / 0.967);
      return {
        grossAmount,
        withholdingAmount: roundWon(grossAmount - amount),
        netAmount: roundWon(amount),
      };
    }

    const grossAmount = roundWon(amount);
    const withholdingAmount = roundWon(grossAmount * 0.033);
    return {
      grossAmount,
      withholdingAmount,
      netAmount: roundWon(grossAmount - withholdingAmount),
    };
  },
  seo: {
    title: '3.3% 원천징수 계산기 | 세전 금액·실수령액 역산',
    description: '3.3% 원천징수 기준으로 세전 금액에서 실수령액을 계산하거나 실수령액에서 세전 금액을 역산합니다.',
    canonicalPath: '/calculators/withholding-33',
  },
  related: ['supply-price-reverse', 'vat'],
};

export const discountMarginCalculator: CalculatorDefinition = {
  id: 'discount-margin',
  slug: 'discount-margin',
  title: '할인 후 마진 계산기',
  description: '정상 판매가, 원가, 할인율을 입력해 할인 판매가와 할인 후 마진율을 계산합니다.',
  category: 'pricing',
  aliases: ['할인 마진', '할인 후 마진', '할인율', '세일 마진', '할인가 이익률'],
  inputs: [
    { id: 'listPrice', label: '정상 판매가', type: 'number', unit: '원', min: 1, step: 100 },
    { id: 'cost', label: '원가', type: 'number', unit: '원', min: 0, step: 100 },
    { id: 'discountRate', label: '할인율', type: 'percentage', unit: '%', min: 0, max: 100, step: 0.1 },
  ],
  outputs: [
    { id: 'marginRate', label: '할인 후 마진율', unit: '%', format: 'percentage', primary: true },
    { id: 'discountedPrice', label: '할인 판매가', unit: '원', format: 'currency' },
    { id: 'discountAmount', label: '할인 금액', unit: '원', format: 'currency' },
    { id: 'profit', label: '할인 후 이익', unit: '원', format: 'currency' },
  ],
  calculate: (values) => {
    const listPrice = numberValue(values.listPrice);
    const cost = numberValue(values.cost);
    const discountRate = numberValue(values.discountRate);
    const discountedPrice = roundWon(listPrice * (1 - discountRate / 100));
    const discountAmount = roundWon(listPrice - discountedPrice);
    const profit = roundWon(discountedPrice - cost);

    return {
      marginRate: discountedPrice === 0 ? null : (profit / discountedPrice) * 100,
      discountedPrice,
      discountAmount,
      profit,
    };
  },
  seo: {
    title: '할인 후 마진 계산기 | 할인 판매가·이익률 계산',
    description: '정상 판매가와 원가, 할인율을 입력하면 할인 판매가, 할인 금액, 이익, 할인 후 마진율을 계산합니다.',
    canonicalPath: '/calculators/discount-margin',
  },
  related: ['margin', 'target-margin-price', 'break-even-roas'],
};

export const targetProfitRevenueCalculator: CalculatorDefinition = {
  id: 'target-profit-revenue',
  slug: 'target-profit-revenue',
  title: '목표이익 필요매출 계산기',
  description: '고정비, 변동비율, 목표이익을 기준으로 목표이익 달성에 필요한 매출을 계산합니다.',
  category: 'business',
  aliases: ['목표이익', '필요매출', '목표 매출', '목표이익 매출', 'CVP'],
  inputs: [
    { id: 'fixedCost', label: '고정비', type: 'number', unit: '원', min: 0, step: 1000 },
    { id: 'variableCostRate', label: '변동비율', type: 'percentage', unit: '%', min: 0, max: 99.9, step: 0.1 },
    { id: 'targetProfit', label: '목표이익', type: 'number', unit: '원', min: 0, step: 1000 },
  ],
  outputs: [
    { id: 'requiredRevenue', label: '필요 매출', unit: '원', format: 'currency', primary: true },
    { id: 'requiredContribution', label: '필요 공헌이익', unit: '원', format: 'currency' },
    { id: 'contributionMarginRate', label: '공헌이익률', unit: '%', format: 'percentage' },
  ],
  calculate: (values) => {
    const fixedCost = numberValue(values.fixedCost);
    const variableCostRate = numberValue(values.variableCostRate);
    const targetProfit = numberValue(values.targetProfit);
    const contributionMarginRate = 100 - variableCostRate;
    const requiredContribution = fixedCost + targetProfit;
    const requiredRevenue = requiredContribution / (contributionMarginRate / 100);

    return {
      requiredRevenue: roundWon(requiredRevenue),
      requiredContribution: roundWon(requiredContribution),
      contributionMarginRate,
    };
  },
  seo: {
    title: '목표이익 필요매출 계산기 | 목표이익 달성 매출 계산',
    description: '고정비와 변동비율, 목표이익을 입력하면 목표이익 달성에 필요한 매출과 공헌이익을 계산합니다.',
    canonicalPath: '/calculators/target-profit-revenue',
  },
  related: ['break-even', 'margin', 'target-margin-price'],
};

export const breakEvenRoasCalculator: CalculatorDefinition = {
  id: 'break-even-roas',
  slug: 'break-even-roas',
  title: '손익분기 ROAS 계산기',
  description: '마진율을 기준으로 광고비를 회수하기 위한 최소 손익분기 ROAS를 계산합니다.',
  category: 'advertising',
  aliases: ['손익분기 ROAS', 'ROAS 손익분기', '광고 손익분기', '최소 ROAS', '광고비 한도'],
  inputs: [
    { id: 'marginRate', label: '마진율', type: 'percentage', unit: '%', min: 0.1, max: 100, step: 0.1 },
  ],
  outputs: [
    { id: 'breakEvenRoas', label: '손익분기 ROAS', unit: '%', format: 'percentage', primary: true },
    { id: 'revenuePerAdWon', label: '광고비 1원당 필요 매출', unit: '배', format: 'decimal' },
    { id: 'maxAdCostRate', label: '매출 대비 최대 광고비', unit: '%', format: 'percentage' },
  ],
  calculate: (values) => {
    const marginRate = numberValue(values.marginRate);
    const revenuePerAdWon = 1 / (marginRate / 100);
    return {
      breakEvenRoas: revenuePerAdWon * 100,
      revenuePerAdWon,
      maxAdCostRate: marginRate,
    };
  },
  seo: {
    title: '손익분기 ROAS 계산기 | 마진율 기준 최소 ROAS 계산',
    description: '마진율을 입력하면 광고비를 회수하기 위해 필요한 최소 손익분기 ROAS와 매출 대비 최대 광고비 비중을 계산합니다.',
    canonicalPath: '/calculators/break-even-roas',
  },
  related: ['roas', 'margin', 'discount-margin'],
};

export const phase3CalculatorDefinitions: readonly CalculatorDefinition[] = [
  supplyPriceReverseCalculator,
  withholding33Calculator,
  discountMarginCalculator,
  targetProfitRevenueCalculator,
  breakEvenRoasCalculator,
];
